import React, { useMemo, useState } from 'react'
import styled from 'styled-components'
import { Form, Input } from 'semantic-ui-react'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { PageHeader } from 'components'

const PageWrap = styled.div`
    max-width: 760px;
    margin: 20px auto;
    padding: 0 16px 24px;
`

const Card = styled.div`
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 4px 24px rgba(13, 24, 40, 0.08);
    padding: 28px 26px;
`

const NestedBlock = styled.div`
    border-left: 3px solid #d3dce8;
    margin-top: 18px;
    margin-left: 6px;
    padding-left: 16px;
`

const HelpText = styled.p`
    color: #5f6f8a;
    margin-top: 2px;
    margin-bottom: 12px;
    font-size: 1.02rem;
`

const QUESTION_TREE = [
    {
        id: 'totalEmployees',
        label: 'Number of employees',
        type: 'number',
        placeholder: 'e.g. 120',
        children: [
            {
                id: 'maleCount',
                label: 'Number of males',
                type: 'number',
                placeholder: '0',
                parentId: 'totalEmployees',
                showWhen: values => Number(values.totalEmployees || 0) > 0,
                children: [
                    {
                        id: 'maleManagers',
                        label: 'Males in people-manager roles',
                        type: 'number',
                        placeholder: '0',
                        parentId: 'maleCount',
                        showWhen: values => Number(values.maleCount || 0) > 0,
                    },
                    {
                        id: 'maleNewHires',
                        label: 'Male new hires (last 12 months)',
                        type: 'number',
                        placeholder: '0',
                        parentId: 'maleCount',
                        showWhen: values => Number(values.maleCount || 0) > 0,
                    },
                ],
            },
            {
                id: 'femaleCount',
                label: 'Number of females',
                type: 'number',
                placeholder: '0',
                parentId: 'totalEmployees',
                showWhen: values => Number(values.totalEmployees || 0) > 0,
                children: [
                    {
                        id: 'femaleManagers',
                        label: 'Females in people-manager roles',
                        type: 'number',
                        placeholder: '0',
                        parentId: 'femaleCount',
                        showWhen: values => Number(values.femaleCount || 0) > 0,
                    },
                    {
                        id: 'femalePartTime',
                        label: 'Females on part-time or job-share contracts',
                        type: 'number',
                        placeholder: '0',
                        parentId: 'femaleCount',
                        showWhen: values => Number(values.femaleCount || 0) > 0,
                    },
                ],
            },
        ],
    },
]

const clearBranch = (questions, targetId, currentValues) => {
    const nextValues = { ...currentValues }
    const walk = nodes => {
        nodes.forEach(node => {
            if (node.id === targetId) {
                (node.children || []).forEach(child => {
                    delete nextValues[child.id]
                    walk([child])
                })
                return
            }
            walk(node.children || [])
        })
    }
    walk(questions)
    return nextValues
}

const renderNode = (node, values, onChange) => {
    const shouldShow = node.showWhen ? node.showWhen(values) : true
    if (!shouldShow) return null
    const childItems = (node.children || [])
        .map(child => renderNode(child, values, onChange))
        .filter(Boolean)

    return (
        <div key={node.id} className="mb-3">
            <Form.Field>
                <label>{node.label}</label>
                <Input
                    type={node.type || 'text'}
                    placeholder={node.placeholder || ''}
                    value={values[node.id] ?? ''}
                    onChange={(_, data) => onChange(node, data.value)}
                />
            </Form.Field>
            {childItems.length > 0 && <NestedBlock>{childItems}</NestedBlock>}
        </div>
    )
}

export const NestedQuestionnaireScreen = () => {
    const [values, setValues] = useState({})
    const [message, setMessage] = useState('')

    const total = Number(values.totalEmployees || 0)
    const male = Number(values.maleCount || 0)
    const female = Number(values.femaleCount || 0)

    const validationMessage = useMemo(() => {
        if (total <= 0) return ''
        if (male + female === total) return 'Male + female matches total employees.'
        return 'Male and female should add up to total employees.'
    }, [total, male, female])

    const handleChange = (node, nextValue) => {
        let normalized = nextValue
        if (node.type === 'number') {
            if (nextValue === '') {
                normalized = ''
            } else {
                normalized = Math.max(0, Number(nextValue) || 0)
            }
        }
        let nextValues = { ...values, [node.id]: normalized }
        if (node.children && (normalized === '' || Number(normalized) <= 0)) {
            nextValues = clearBranch(QUESTION_TREE, node.id, nextValues)
        }
        setValues(nextValues)
        setMessage('')
    }

    const handleValidate = () => {
        setMessage(validationMessage || 'Questionnaire is valid.')
    }

    const handleClear = () => {
        setValues({})
        setMessage('')
    }

    return (
        <PageWrap>
            <Card>
                <PageHeader header="Organization snapshot" />
                <HelpText>
                    One screen - follow-up questions open in a nested tree when your answers qualify.
                </HelpText>
                <Form noValidate>
                    {QUESTION_TREE.map(node => renderNode(node, values, handleChange))}
                </Form>
                {validationMessage && (
                    <HelpText style={{ marginTop: 8, marginBottom: 10 }}>
                        {validationMessage}
                    </HelpText>
                )}
                {message && (
                    <HelpText style={{ marginTop: 4, marginBottom: 12 }}>
                        {message}
                    </HelpText>
                )}
                <div className="d-flex gap-2 mt-3">
                    <AppPrimaryButton type="button" onClick={handleValidate}>
                        Validate & review
                    </AppPrimaryButton>
                    <AppSecondaryButton type="button" onClick={handleClear}>
                        Clear
                    </AppSecondaryButton>
                </div>
            </Card>
        </PageWrap>
    )
}

export default NestedQuestionnaireScreen
